import React, { useState, useEffect, useRef } from 'react';
import { Search, Trash2, Pill, XCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
    getPetMedications,
    createMedication,
    updateMedication,
    deleteMedication,
    discontinueMedication
} from '../../utils/medicationsApi';
import { getMedicationCatalog } from '../../utils/medicationCatalogApi';
import { Medication, MedicationCatalogItem, MedicationStatus } from '../../types/medication';
import ConfirmDialog from '../common/ConfirmDialog';

interface PetMedicationsProps {
    petId: string;
}

const PetMedications: React.FC<PetMedicationsProps> = ({ petId }) => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [medications, setMedications] = useState<Medication[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<MedicationCatalogItem[]>([]);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [medicationToDelete, setMedicationToDelete] = useState<Medication | null>(null);
    const [showDiscontinueConfirm, setShowDiscontinueConfirm] = useState(false);
    const [medicationToDiscontinue, setMedicationToDiscontinue] = useState<Medication | null>(null);
    const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<MedicationCatalogItem | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const [focusedId, setFocusedId] = useState<string | null>(null);

    useEffect(() => {
        fetchMedications();
    }, [petId]);

    // Deep-link focus support: /medications?focus=Carprofen
    useEffect(() => {
        if (loading) return;
        const focus = (searchParams.get('focus') || '').trim().toLowerCase();
        if (!focus || medications.length === 0) return;

        const match = medications.find((m) => (m.name || '').toLowerCase().includes(focus));
        if (!match) return;

        setFocusedId(match.id);
        const el = rowRefs.current[match.id];
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        const t = window.setTimeout(() => setFocusedId(null), 3500);
        return () => window.clearTimeout(t);
    }, [loading, medications, searchParams]);

    // Expose handler for Dashboard to trigger the add medication modal
    useEffect(() => {
        (window as any)._medicationsAddHandler = () => {
            setShowSearchModal(true);
        };
        return () => {
            delete (window as any)._medicationsAddHandler;
        };
    }, []);

    useEffect(() => {
        if (searchQuery.length > 1 && user?.practice_id && showSearchModal) {
            searchMedications();
        } else {
            setSearchResults([]);
        }
    }, [searchQuery, user?.practice_id, showSearchModal]);

    const fetchMedications = async () => {
        try {
            setLoading(true);
            const data = await getPetMedications(petId);
            setMedications(data);
        } catch (error) {
            console.error('Failed to fetch medications:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchMedications = async () => {
        if (!user?.practice_id) return;
        try {
            const results = await getMedicationCatalog(user.practice_id, searchQuery, true);
            setSearchResults(results);
        } catch (error) {
            console.error('Failed to search medications:', error);
        }
    };

    const handleSelectCatalogItem = (item: MedicationCatalogItem) => {
        setSelectedCatalogItem(item);
        setShowAddModal(true);
        setSearchQuery('');
        setShowSearchModal(false);
    };

    const handleAddMedication = async (formData: {
        name: string;
        dose?: string;
        dose_unit?: string;
        frequency?: string;
        quantity?: number;
        refills?: number;
        instructions?: string;
        notes?: string;
    }) => {
        try {
            await createMedication(petId, {
                ...formData,
                status: 'ACTIVE'
            });
            setShowAddModal(false);
            setSelectedCatalogItem(null);
            fetchMedications();
        } catch (error) {
            console.error('Failed to add medication:', error);
            alert('Failed to add medication');
        }
    };

    const handleDeleteMedication = (medication: Medication) => {
        setMedicationToDelete(medication);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteMedication = async () => {
        if (!medicationToDelete) return;

        try {
            await deleteMedication(medicationToDelete.id);
            fetchMedications();
        } catch (error) {
            console.error('Failed to delete medication:', error);
            alert('Failed to delete medication');
        } finally {
            setShowDeleteConfirm(false);
            setMedicationToDelete(null);
        }
    };

    const handleDiscontinueMedication = (medication: Medication) => {
        setMedicationToDiscontinue(medication);
        setShowDiscontinueConfirm(true);
    };

    const confirmDiscontinueMedication = async () => {
        if (!medicationToDiscontinue) return;

        try {
            await discontinueMedication(medicationToDiscontinue.id, 'Discontinued by provider');
            fetchMedications();
        } catch (error) {
            console.error('Failed to discontinue medication:', error);
            alert('Failed to discontinue medication');
        } finally {
            setShowDiscontinueConfirm(false);
            setMedicationToDiscontinue(null);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—';

        // If backend sends a date-only string, treat it as a LOCAL date to avoid UTC shifts.
        // (e.g. "2025-12-13" should render as Dec 13 in the user's timezone)
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
        const d = dateOnlyMatch
            ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
            : new Date(dateString);

        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusBadge = (status: MedicationStatus) => {
        const styles: Record<MedicationStatus, string> = {
            'ACTIVE': 'bg-green-100 text-green-800',
            'COMPLETED': 'bg-gray-100 text-gray-800',
            'DISCONTINUED': 'bg-red-100 text-red-800',
            'EXPIRED': 'bg-yellow-100 text-yellow-800'
        };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) return <div className="p-8 text-center">Loading medications...</div>;

    return (
        <div className="space-y-6">
            {/* Medications Table */}
            <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden">
                {medications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <Pill className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No medications on record.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Medication</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Dose</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Frequency</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-16">Qty</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-16">Refills</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Prescribed</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Expires</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Encounter</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Status</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-center w-24">Discontinue</th>
                                        <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-center w-16">Delete</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {medications.map((med) => (
                                        <tr
                                            key={med.id}
                                            ref={(el) => { rowRefs.current[med.id] = el; }}
                                            className={`hover:bg-gray-50 group ${focusedId === med.id ? 'bg-yellow-50' : 'bg-white'}`}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-gray-900">{med.name}</div>
                                                {med.instructions && (
                                                    <div className="text-xs text-gray-500 truncate max-w-xs" title={med.instructions}>
                                                        {med.instructions}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {med.dose && med.dose_unit ? `${med.dose} ${med.dose_unit}` : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {med.frequency || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                                                {med.quantity ?? '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                                                {med.refills ?? '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {formatDate(med.prescribed_date)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {formatDate(med.expires_date)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {med.encounter_id ? (
                                                    <a
                                                        href={`/pet/${petId}/encounter/${med.encounter_id}/intake`}
                                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-none hover:bg-blue-100 transition-colors whitespace-nowrap"
                                                    >
                                                        {med.encounter_date ? new Date(med.encounter_date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : 'Encounter'}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(med.status)}`}>
                                                    {med.status}
                                                </span>
                                            </td>
                                            {/* Discontinue Column */}
                                            <td className="px-4 py-3 text-center">
                                                {med.status === 'ACTIVE' && (
                                                    <button
                                                        onClick={() => handleDiscontinueMedication(med)}
                                                        className="p-1 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                                                        title="Discontinue"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </td>
                                            {/* Delete Column */}
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleDeleteMedication(med)}
                                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>

            {/* Add Medication Modal */}
            {showAddModal && selectedCatalogItem && (
                <AddMedicationModal
                    catalogItem={selectedCatalogItem}
                    onClose={() => {
                        setShowAddModal(false);
                        setSelectedCatalogItem(null);
                    }}
                    onSubmit={handleAddMedication}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setMedicationToDelete(null);
                }}
                onConfirm={confirmDeleteMedication}
                title="Delete Medication"
                message={`Are you sure you want to delete "${medicationToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                isDangerous={true}
            />

            {/* Discontinue Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showDiscontinueConfirm}
                onClose={() => {
                    setShowDiscontinueConfirm(false);
                    setMedicationToDiscontinue(null);
                }}
                onConfirm={confirmDiscontinueMedication}
                title="Discontinue Medication"
                message={`Are you sure you want to discontinue "${medicationToDiscontinue?.name}"? The medication will be marked as discontinued but not deleted.`}
                confirmText="Discontinue"
                isDangerous={true}
            />

            {/* Search Medication Modal */}
            {showSearchModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-none shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900">Add Medication</h2>
                            <button
                                onClick={() => {
                                    setShowSearchModal(false);
                                    setSearchQuery('');
                                    setSearchResults([]);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search for medication..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                            {searchQuery.length < 2 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <Pill className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                    <p className="text-sm">Type to search medications...</p>
                                </div>
                            ) : searchResults.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-sm">No medications found</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {searchResults.map((item) => (
                                        <div
                                            key={item.id}
                                            onClick={() => handleSelectCatalogItem(item)}
                                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-none flex justify-between items-center border border-gray-100"
                                        >
                                            <div>
                                                <span className="font-medium text-gray-900">{item.name}</span>
                                                {item.default_dose && item.default_dose_unit && (
                                                    <span className="ml-2 text-sm text-gray-500">
                                                        {item.default_dose} {item.default_dose_unit}
                                                    </span>
                                                )}
                                            </div>
                                            {item.default_frequency && (
                                                <span className="text-xs text-gray-500">{item.default_frequency}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Add Medication Modal Component
interface AddMedicationModalProps {
    catalogItem: MedicationCatalogItem;
    onClose: () => void;
    onSubmit: (data: {
        name: string;
        dose?: string;
        dose_unit?: string;
        frequency?: string;
        quantity?: number;
        refills?: number;
        instructions?: string;
        notes?: string;
    }) => Promise<void>;
}

const AddMedicationModal: React.FC<AddMedicationModalProps> = ({ catalogItem, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: catalogItem.name,
        dose: catalogItem.default_dose || '',
        dose_unit: catalogItem.default_dose_unit || '',
        frequency: catalogItem.default_frequency || '',
        quantity: catalogItem.default_quantity || 30,
        refills: catalogItem.default_refills || 0,
        instructions: catalogItem.instructions || '',
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await onSubmit({
                ...formData,
                quantity: formData.quantity || undefined,
                refills: formData.refills || undefined
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-none shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Add Medication</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Medication Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Dose</label>
                            <input
                                type="text"
                                value={formData.dose}
                                onChange={(e) => setFormData({ ...formData, dose: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., 1"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                            <input
                                type="text"
                                value={formData.dose_unit}
                                onChange={(e) => setFormData({ ...formData, dose_unit: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., tablet"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                        <select
                            value={formData.frequency}
                            onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select frequency</option>
                            <option value="Once daily">Once daily</option>
                            <option value="Twice daily (BID)">Twice daily (BID)</option>
                            <option value="Three times daily (TID)">Three times daily (TID)</option>
                            <option value="Four times daily (QID)">Four times daily (QID)</option>
                            <option value="Every other day">Every other day</option>
                            <option value="Weekly">Weekly</option>
                            <option value="As needed (PRN)">As needed (PRN)</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                            <input
                                type="number"
                                min="1"
                                value={formData.quantity}
                                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Refills</label>
                            <input
                                type="number"
                                min="0"
                                value={formData.refills}
                                onChange={(e) => setFormData({ ...formData, refills: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Patient Instructions</label>
                        <textarea
                            value={formData.instructions}
                            onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Instructions for the pet owner..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Notes for staff..."
                        />
                    </div>
                </form>

                <div className="flex justify-end gap-3 p-4 border-t border-gray-100 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-none hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-none hover:bg-blue-700 disabled:opacity-50"
                    >
                        {submitting ? 'Adding...' : 'Add Medication'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PetMedications;

